import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Bell, CheckCheck } from 'lucide-react';
import type { NotificationResponse } from '@proerp/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ScrollArea,
} from '@proerp/ui';
import { useToasts } from '@/lib/toast';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/features/notifications/notificationsApi';

/**
 * Benachrichtigungs-Glocke im Header. Zeigt die Anzahl ungelesener
 * Benachrichtigungen als Badge und listet im Aufklapp-Menü die neuesten —
 * ein Klick markiert sie als gelesen und springt zum hinterlegten Ziel
 * (`linkPath`). Daten werden gepollt (TanStack Query, siehe notificationsApi).
 */
export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const push = useToasts((state) => state.push);
  const { data: countData } = useUnreadNotificationCount();
  const { data: listData, isLoading } = useNotifications({ limit: 10 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = countData?.unread ?? 0;
  const notifications = listData?.data ?? [];

  // Toast bei neu eingetroffener Benachrichtigung: neuesten createdAt merken und
  // beim Polling auf echte Neuzugänge prüfen. Der erste Ladevorgang toastet nicht
  // (sonst käme beim Öffnen der App für jede Bestands-Meldung ein Toast).
  const lastSeenAt = useRef<number | null>(null);
  useEffect(() => {
    const newest = notifications[0];
    if (!newest) return;
    const newestAt = new Date(newest.createdAt).getTime();
    if (lastSeenAt.current === null) {
      lastSeenAt.current = newestAt;
      return;
    }
    if (newestAt > lastSeenAt.current && !newest.readAt) {
      push('info', newest.title);
    }
    lastSeenAt.current = Math.max(lastSeenAt.current, newestAt);
  }, [notifications, push]);

  const handleSelect = (notification: NotificationResponse): void => {
    if (!notification.readAt) {
      markRead.mutate(notification.id);
    }
    if (notification.linkPath) {
      navigate(notification.linkPath);
    }
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label={
            unread > 0 ? `Benachrichtigungen — ${unread} ungelesen` : 'Benachrichtigungen'
          }
        >
          <Bell strokeWidth={1.5} />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[0.625rem] font-semibold leading-none text-destructive-foreground"
              aria-hidden
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium text-foreground">Benachrichtigungen</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            disabled={unread === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            <CheckCheck className="size-3.5" strokeWidth={1.5} aria-hidden />
            Alle gelesen
          </Button>
        </div>

        {isLoading ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">Wird geladen …</p>
        ) : notifications.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            Keine Benachrichtigungen.
          </p>
        ) : (
          <ScrollArea className="max-h-80">
            <ul className="divide-y">
              {notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(notification)}
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                  >
                    <span
                      className={
                        notification.readAt
                          ? 'mt-1.5 size-2 shrink-0 rounded-full bg-transparent'
                          : 'mt-1.5 size-2 shrink-0 rounded-full bg-primary'
                      }
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={
                          notification.readAt
                            ? 'block truncate text-sm text-foreground'
                            : 'block truncate text-sm font-medium text-foreground'
                        }
                      >
                        {notification.title}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {notification.body}
                      </span>
                      <span className="mt-0.5 block text-[0.6875rem] text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                          locale: de,
                        })}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}

        <div className="border-t px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full justify-center text-xs"
            onClick={() => {
              navigate('/benachrichtigungen');
              setIsOpen(false);
            }}
          >
            Alle ansehen
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
